<template>
  <div
    v-if="tabs?.length"
    ref="container"
    class="full-width-container pretty-tabs-nav-container"
  >
    <TabsNav
      ref="tabs"
      class="mb-0 flex pretty-tabs-nav"
      v-bind="navTabsProps"
      :is-member="isMember"
      @on-tab-change="onTabChange"
    >
      <nuxt-link
        v-for="tab in tabs"
        :key="tab.text"
        class="pretty-tabs-nav-link inline-flex justify-center items-center h-full mb-0 text-center font-semibold"
        :to="createNuxtLinkToObj(tab)"
        :class="{ 'active': isActiveTab(tab.localeUrlKey) }"
        role="tab"
        :aria-selected="isActiveTab(tab.localeUrlKey)"
      >
        <div
          class="tabs-text text-base font-semibold flex items-center pr-2"
          :class="{
            'text-blue-800': isActiveTab(tab.localeUrlKey),
            'pl-2': tab.icon,
          }"
          :data-i18n="tab?.translationKey || tab?.text"
        >
          <div
            v-if="tab.icon"
            class="w-6 flex justify-center items-center"
          >
            <component
              :is="tab.icon"
              v-if="isVueComponent(tab.icon)"
              class="h-[19px] -mt-0.5 -ml-2"
              :font-controlled="false"
            />
            <fa
              v-else
              :icon="tab.icon"
              size="lg"
              class="mb-[1px] mr-2"
            />
          </div>
          <span
            class="tab-text-content"
            :class="[
              {
                'ml-2 md:ml-0': tab.icon,
                'border-b-2 border-transparent hover:border-black': !isActiveTab(tab.localeUrlKey)
              }
            ]"
          >
            {{ $t(tab.text) }}
          </span>
        </div>
      </nuxt-link>
    </TabsNav>
  </div>
</template>

<script>
import TabsNav from '@layers/web/components/common/TabsNav'

export default defineNuxtComponent({
  setup () {
    const { urls: localeUrlsRaw } = useLocale()
    const route = useRoute()
    const slug = route.params.slug
    return {
      localeUrlsRaw,
      slug,
    }
  },

  props: {
    ...TabsNav.props,

    /**
     * @prop {Array<
     *   text: string
     *   to: string | object
     *   localeUrlKey: string
     * >} tabs
     */
    tabs: {
      type: Array,
      default: () => [],
    },

    /**
     * @prop {Boolean} keepParams
     * Will spread all params into nuxt-link params, including those you specify in props.tabs[].to.query (if object)
     */
    keepQueryParams: {
      type: Boolean,
      default: false,
    },

    isSol: {
      type: Boolean,
      default: false,
    },

    isMember: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['onTabChange'],

  data () {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
    const { tabs, isMember, ...navTabsProps } = this.$props

    const activeTab = this.isMember ? findKeyBySlug(this.slug, this.localeUrlsRaw.authSlug) : findActiveSlugKey(this.localeUrls, this.$route.params)

    return {
      navTabsProps,
      activeTab: activeTab || this.tabs?.[0]?.localeUrlKey || '',
    }
  },

  methods: {
    onTabChange (newActiveTab) {
      this.activeTab = newActiveTab
      this.$emit('onTabChange', newActiveTab)
    },

    isActiveTab (tab) {
      return this.activeTab === tab
    },

    createNuxtLinkToObj (tab) {
      let to = tab.to

      if (this.keepQueryParams) {
        switch (typeof tab.to) {
          case 'object':
            to.query = {
              ...this.$route.query,
              ...(to.params || {}),
            }
            break

          case 'string':
            to = {}
            to.query = this.$route.query
            to.path = tab.to
            break
        }
      }

      return to
    },
  },
})
</script>

<style lang="scss" scoped>
$prettyNavBg: theme('colors.blue.100');

.pretty-tabs-nav-container {
  letter-spacing: 0.5px;
  position: relative;

  &:before {
    content: '';
    height: 100%;
    width: 100%;
    position: absolute;
    left: 0;
    z-index: -1;
  }

  .pretty-tabs-nav-link {
    color: theme('colors.black');
  }

  .pretty-tabs-nav-link.active {
  .tab-text-content {
    border-bottom: 2px solid theme('colors.blue.800');
    color: theme('colors.blue.800');
  }
}

  @each $breakpoint in map-keys($breakpoints) {
    @include media-breakpoint-up($breakpoint, $breakpoints) {
      $infix: breakpoint-infix($breakpoint, $breakpoints);

      &.pretty-tabs-layout#{$infix} {
        &-row {
          background: inherit;

          .pretty-tabs-nav {
            flex-direction: row;
            height: 50px;
            @apply pt-4;
          }

          ul {
            a {
              padding-top: .5rem !important;
              padding-bottom: .5rem !important;
              margin-right: 1.5rem;

              div {
                @apply py-1;
              }
            }
          }

          .pretty-tabs-nav-link.active {
            &:before {
              display: none;
            }
          }
        }

        &-column {
          background: theme('colors.gray.300');

          .pretty-tabs-nav {
            flex-direction: column;
            height: unset;
          }

          .pretty-tabs-nav-link {
            padding: 8px 0 8px 0;
            justify-content: start;

            &:first-child {
              padding-top: 14px;
            }
            &:last-child {
              padding-bottom: 14px;
            }

            &.active {
              clip-path: inset(0 -50vw);

              &:before {
                content: '';
                left: 0;
                width: 100vw;
                height: 400px;
                position: absolute;
                z-index: -1;
              }
            }
          }
        }
      }
    }
  }

  &__destinations-tabs {
    clip-path: inset(0 -50vw 0 -50vw);
    letter-spacing: 0.5px;
    max-width: 100%;

    &:before {
      content: '';
      height: 100vh;
      width: 100vw;
      position: absolute;
      left: 0;
      z-index: -1;
      background: theme('colors.light-turquoise');
    }

    .pretty-tabs-nav-link.active {
      clip-path: inset(0 -50vw);

      .tabs-text {
        font-size: 1rem;
        font-weight: bold !important;
        color: theme('colors.medium-blue');
      }
    }
  }
}
</style>
